import type { Schemas } from "@kollectivia/shared";
import { Comparison } from "@kollectivia/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { api, ApiException } from "../../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../../lib/apiErrorMessage";

type Preview = {
  document: Comparison.FrozenComparison & {
    title: string;
    identity?: { cabinet: string; company: string; reference: string; campaign: string };
  };
  snapshot_sha256: string;
};
export function ComparisonExports({
  projectId,
  quoteRequestId,
  charge,
}: {
  projectId: string;
  quoteRequestId?: string;
  charge: Comparison.ComparisonCharge;
}) {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [offers, setOffers] = useState<string[]>([]);
  const [fields, setFields] = useState<string[]>([]);
  const [audience, setAudience] = useState<"" | "advisor" | "client">("");
  const [notes, setNotes] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());
  const [preview, setPreview] = useState<Preview | null>(null);
  const client = useQueryClient();
  const language = normalizeLang(i18n.resolvedLanguage);
  const input = (): Schemas.CreateComparisonExport => ({
    request_id: requestId,
    project_id: projectId,
    quote_request_id: quoteRequestId!,
    submission_ids: offers,
    field_keys: fields,
    audience: audience as "advisor" | "client",
    language,
    include_notes: notes,
    confirmed: true,
  });
  const reset = () => {
    setPreview(null);
    setConfirmed(false);
    setRequestId(crypto.randomUUID());
  };
  const prepare = useMutation({
    mutationFn: () => api.post<Preview>("/v1/comparison-exports/preview", input()),
    onSuccess: (result) => {
      setPreview(result);
      setConfirmed(false);
      save.reset();
    },
  });
  const save = useMutation({
    mutationFn: () =>
      api.post<Schemas.ComparisonExportRow>("/v1/comparison-exports", {
        ...input(),
        expected_snapshot_sha256: preview?.snapshot_sha256,
      }),
    onSuccess: () => {
      reset();
      void client.invalidateQueries({ queryKey: ["comparison-exports", projectId] });
    },
  });
  const history = useQuery({
    queryKey: ["comparison-exports", projectId],
    enabled: expanded,
    queryFn: () =>
      api.get<Schemas.ComparisonExportRow[]>(`/v1/comparison-exports?project_id=${projectId}`),
    retry: false,
  });
  const download = useMutation({
    mutationFn: ({
      id,
      format,
      version,
    }: {
      id: string;
      format: "pdf" | "xlsx";
      version: number;
    }) =>
      api.download(`/v1/comparison-exports/${id}/${format}`, `comparison-v${version}.${format}`),
  });
  const busy = prepare.isPending || save.isPending;
  const eligible = Comparison.projectReport(charge, audience || "advisor").rows;
  const error = prepare.error ?? save.error ?? download.error ?? history.error;
  const errorText =
    error instanceof ApiException ? getApiErrorMessage(error, language) : t("errors.generic");
  const toggle = (items: string[], item: string) =>
    items.includes(item) ? items.filter((x) => x !== item) : [...items, item];
  return (
    <details
      className="my-4 rounded-xl border border-slate-200 bg-white p-4"
      onToggle={(event) => setExpanded(event.currentTarget.open)}
    >
      <summary
        data-guide-target="comparison-exports"
        className="min-h-11 cursor-pointer font-semibold"
      >
        {t("comparison_exports.title")}
      </summary>
      <p className="my-3 text-sm text-slate-600">{t("comparison_exports.scope")}</p>
      {!quoteRequestId ? (
        <p role="status">{t("comparison_exports.choose_campaign")}</p>
      ) : (
        <>
          <fieldset disabled={busy} className="space-y-3">
            <label className="block">
              {t("comparison_exports.audience")}
              <select
                className="ml-3 min-h-11 rounded border px-3"
                value={audience}
                onChange={(e) => {
                  setAudience(e.target.value as typeof audience);
                  setNotes(false);
                  setFields([]);
                  reset();
                }}
              >
                <option value="">{t("comparison_exports.choose")}</option>
                <option value="advisor">{t("comparison_exports.advisor")}</option>
                <option value="client">{t("comparison_exports.client")}</option>
              </select>
            </label>
            <fieldset>
              <legend className="font-medium">{t("comparison_exports.offers")}</legend>
              {(charge.submissions ?? []).map((offer, sourceIndex) => (
                <label key={offer.id} className="flex min-h-11 items-center gap-3">
                  <input
                    type="checkbox"
                    checked={offers.includes(offer.id)}
                    onChange={() => {
                      setOffers(toggle(offers, offer.id));
                      reset();
                    }}
                  />
                  {Comparison.comparisonOfferLabel(offer, sourceIndex, language)}
                </label>
              ))}
              <ol>
                {offers.map((id, index) => (
                  <li key={id} className="flex min-h-11 items-center justify-between gap-2">
                    <span>
                      {index + 1}.{" "}
                      {(() => {
                        const sourceIndex = charge.submissions?.findIndex((s) => s.id === id) ?? -1;
                        const offer = charge.submissions?.[sourceIndex];
                        return offer
                          ? Comparison.comparisonOfferLabel(offer, sourceIndex, language)
                          : "";
                      })()}
                    </span>
                    {index > 0 && (
                      <button
                        type="button"
                        className="btn-ghost min-h-11"
                        onClick={() => {
                          const next = [...offers];
                          [next[index - 1], next[index]] = [next[index]!, next[index - 1]!];
                          setOffers(next);
                          reset();
                        }}
                      >
                        {t("comparison_exports.move_up")}
                      </button>
                    )}
                  </li>
                ))}
              </ol>
            </fieldset>
            <fieldset>
              <legend className="font-medium">{t("comparison_exports.fields")}</legend>
              <div className="max-h-64 overflow-y-auto">
                {eligible.map((field) => (
                  <label key={field.field_key} className="flex min-h-11 items-center gap-3">
                    <input
                      type="checkbox"
                      checked={fields.includes(field.field_key)}
                      onChange={() => {
                        setFields(toggle(fields, field.field_key));
                        reset();
                      }}
                    />
                    {Comparison.comparisonFieldLabel(field.field_key, language)}
                  </label>
                ))}
              </div>
            </fieldset>
            {audience === "advisor" && (
              <label className="flex min-h-11 items-center gap-3">
                <input
                  type="checkbox"
                  checked={notes}
                  onChange={(e) => {
                    setNotes(e.target.checked);
                    reset();
                  }}
                />
                {t("comparison_exports.notes")}
              </label>
            )}
            <button
              type="button"
              className="btn-ghost min-h-11"
              disabled={!audience || !offers.length || !fields.length}
              onClick={() => prepare.mutate()}
            >
              {t("comparison_exports.preview")}
            </button>
          </fieldset>
          {preview && (
            <section className="my-4 space-y-3" aria-label={t("comparison_exports.preview")}>
              {preview.document.identity && (
                <p className="break-words text-sm">
                  {preview.document.identity.cabinet} · {preview.document.identity.company} ·{" "}
                  {preview.document.identity.reference} · {preview.document.identity.campaign}
                </p>
              )}
              <p className="text-sm">{preview.document.warning}</p>
              <div
                className="overflow-x-auto"
                role="region"
                aria-label={t("comparison_exports.preview")}
                tabIndex={0}
              >
                <table className="w-full text-sm">
                  <thead>
                    <tr>
                      {preview.document.headers.map((h, i) => (
                        <th className="p-3 text-left" key={i}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.document.rows.map((row, i) => (
                      <tr key={i}>
                        {row.map((value, j) => (
                          <td
                            className="min-w-40 whitespace-pre-wrap break-words border-t p-3 align-top"
                            key={j}
                          >
                            {value}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <label className="flex min-h-11 items-center gap-3">
                <input
                  type="checkbox"
                  checked={confirmed}
                  disabled={busy}
                  onChange={(e) => setConfirmed(e.target.checked)}
                />
                {t("comparison_exports.confirm")}
              </label>
              <button
                type="button"
                className="btn-primary min-h-11"
                disabled={!confirmed || busy}
                onClick={() => save.mutate()}
              >
                {t("comparison_exports.create")}
              </button>
            </section>
          )}
        </>
      )}
      {busy && <p role="status">{t("app.loading")}</p>}
      {error && (
        <p role="alert" className="my-3 text-red-700">
          {errorText}
        </p>
      )}
      {save.isSuccess && <p role="status">{t("comparison_exports.saved")}</p>}
      <h3 className="mt-4 font-medium">{t("comparison_exports.history")}</h3>
      <p className="text-sm text-slate-600">{t("comparison_exports.history_hint")}</p>
      {expanded && history.isPending && <p role="status">{t("app.loading")}</p>}
      {history.isSuccess && history.data.length === 0 && (
        <p>{t("comparison_exports.history_empty")}</p>
      )}
      {history.data?.map((exported) => (
        <div key={exported.id} className="flex flex-wrap items-center gap-3 border-t py-3">
          <span>
            v{exported.version} · {t(`comparison_exports.${exported.audience}`)} ·{" "}
            {exported.language.toUpperCase()} · {new Date(exported.created_at).toLocaleString()}
          </span>
          {(["pdf", "xlsx"] as const).map((format) => (
            <button
              type="button"
              className="btn-ghost min-h-11"
              key={format}
              disabled={download.isPending}
              onClick={() =>
                download.mutate({ id: exported.id, version: exported.version, format })
              }
            >
              {format.toUpperCase()}
            </button>
          ))}
        </div>
      ))}
    </details>
  );
}
